import React from "react";

const CentralizedInbox = () => {
  const inboxFeatures = [
    {
      title: "One Shared Dashboard",
      desc: "See every form submission in a single, centralized inbox. Your entire team can access it in real time — no more forwarding emails or juggling accounts.",
      icon: "group",
    },
    {
      title: "Assign, Tag, & Annotate",
      desc: "Tag messages by category, assign leads to team members, and add private internal notes to keep everyone aligned on the next steps.",
      icon: "sell",
    },
    {
      title: "Lead Status Tracking",
      desc: "Know at a glance which inquiries are open, pending, or resolved. Prioritize urgent requests and never let a potential lead slip through the cracks.",
      icon: "query_stats",
    },
  ];

  return (
    <section
      id="centralized-inbox"
      className="py-16 sm:py-20 lg:py-24 bg-slate-50 border-y border-[#E2E8F0] overflow-hidden"
    >
      <div className="mx-auto w-[95%] md:w-[90%]">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
          <div className="w-full lg:w-1/2">
            <h2 className="text-sm sm:text-base font-bold tracking-[0.2em] text-[#E63946] uppercase mb-3 sm:mb-4">
              Communication Management
            </h2>
            <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0B1C2D] lg:text-5xl leading-[1.15]">
              All Your Form Responses.{" "}
              <br className="hidden sm:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0B1C2D] to-[#E63946]">
                One Smart Dashboard.
              </span>
            </h3>

            <p className="mt-5 sm:mt-6 text-base sm:text-lg leading-relaxed text-[#64748B]">
              Every time someone fills out your form, ContactHQ turns that
              submission into a conversation. No more digging through overflowing
              inboxes or lost customer requests. Everything is organized,
              trackable, and actionable in one place.
            </p>

            <div className="mt-8 sm:mt-12 space-y-8 sm:space-y-10">
              {inboxFeatures.map((item, index) => (
                <div
                  key={index}
                  className="group flex gap-4 sm:gap-6 opacity-0 animate-fade-in-up"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className="flex-none flex items-center justify-center size-10 sm:size-12 rounded-xl bg-white text-[#0B1C2D] group-hover:bg-[#0B1C2D] group-hover:text-white transition-all duration-300 shadow-sm border border-[#E2E8F0]">
                    <span className="material-symbols-outlined">
                      {item.icon}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-base sm:text-lg font-bold text-[#0B1C2D] mb-1.5 sm:mb-2">
                      {item.title}
                    </h4>
                    <p className="text-[#64748B] leading-relaxed text-sm lg:text-base">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 sm:mt-12 p-4 bg-[#0B1C2D]/5 rounded-xl border-l-4 border-[#E63946] italic text-sm text-[#0B1C2D] font-medium animate-fade-in-up [animation-delay:600ms]">
              "Think of it as a mix between Slack and Gmail, built specifically
              for managing form submissions efficiently."
            </div>
          </div>

          <div className="w-full lg:w-1/2 relative opacity-0 animate-fade-in-up [animation-delay:400ms] pb-12 sm:pb-10">
            <div className="relative rounded-2xl bg-white p-1 shadow-2xl border border-[#E2E8F0] overflow-hidden">
              <div className="flex bg-white h-[380px] sm:h-[500px]">
                <div className="w-12 sm:w-16 bg-[#0B1C2D] flex flex-col items-center py-5 sm:py-6 gap-5 sm:gap-6 shrink-0">
                  <div className="size-7 sm:size-8 rounded-lg bg-[#E63946] flex items-center justify-center text-white">
                    <span className="material-symbols-outlined text-sm">hub</span>
                  </div>
                  <div className="size-7 sm:size-8 rounded-full bg-white/10"></div>
                  <div className="size-7 sm:size-8 rounded-full bg-white/10"></div>
                </div>

                <div className="flex-1 flex flex-col min-w-0">
                  <div className="p-3 sm:p-4 border-b border-[#E2E8F0] flex justify-between items-center gap-2">
                    <span className="font-bold text-[#0B1C2D] text-sm sm:text-base truncate">
                      Centralized Inbox
                    </span>
                    <div className="flex -space-x-2 shrink-0">
                      <img
                        src="https://i.pravatar.cc/100?u=1"
                        className="size-6 rounded-full border-2 border-white"
                        alt=""
                      />
                      <img
                        src="https://i.pravatar.cc/100?u=2"
                        className="size-6 rounded-full border-2 border-white"
                        alt=""
                      />
                    </div>
                  </div>

                  <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 overflow-hidden">
                    <div className="p-3 sm:p-4 rounded-xl border-2 border-[#E63946]/20 bg-[#E63946]/5 space-y-2">
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-xs font-bold text-[#0B1C2D] truncate">
                          Rahul Kumar
                        </span>
                        <span className="px-2 py-0.5 rounded-full bg-[#E63946] text-white text-[8px] font-black uppercase shrink-0">
                          Urgent
                        </span>
                      </div>
                      <p className="text-[10px] text-[#64748B] line-clamp-1">
                        "I need a contact form for my new SaaS project..."
                      </p>
                      <div className="pt-2 border-t border-[#E63946]/10 flex items-center gap-2">
                        <span className="material-symbols-outlined text-xs text-[#E63946]">
                          notes
                        </span>
                        <span className="text-[9px] font-bold text-[#0B1C2D] truncate">
                          Note: Follow up by Monday
                        </span>
                      </div>
                    </div>

                    {[1, 2].map((i) => (
                      <div
                        key={i}
                        className="p-3 sm:p-4 rounded-xl border border-[#E2E8F0] space-y-2 opacity-60"
                      >
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-bold text-[#0B1C2D]">
                            Inquiry #{402 + i}
                          </span>
                          <span className="text-[9px] text-[#64748B]">2h ago</span>
                        </div>
                        <div className="h-2 w-3/4 bg-slate-100 rounded"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="hidden sm:flex absolute top-1/2 right-4 -translate-y-1/2 flex-col gap-2">
                <div className="bg-white p-3 rounded-xl shadow-lg border border-[#E2E8F0] flex items-center gap-3 animate-float">
                  <div className="size-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                    <span className="material-symbols-outlined text-sm">
                      check
                    </span>
                  </div>
                  <div>
                    <p className="text-[9px] font-black text-[#64748B] uppercase">
                      Resolved
                    </p>
                    <p className="text-xs font-bold text-[#0B1C2D]">
                      +42 this week
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute bottom-0 left-2 sm:left-0 bg-white border border-[#E2E8F0] p-3 sm:p-4 rounded-2xl shadow-xl flex items-center gap-3 animate-float [animation-delay:1s] max-w-[calc(100%-1rem)]">
              <div className="size-9 sm:size-10 shrink-0 rounded-full bg-[#0B1C2D] text-white flex items-center justify-center">
                <span className="material-symbols-outlined">send</span>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-black text-[#64748B] uppercase">
                  Direct Reply
                </div>
                <div className="text-sm font-bold text-[#0B1C2D]">
                  No Email Chaos
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CentralizedInbox;
